import { EventUserFeedback } from "../models/EventUserFeedback";
import OCGatewayClient from "../client/OCGatewayClient";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export function useEventUserFeedbackQuery(deviceId: string, eventId: number) {
    const queryClient = useQueryClient();

    const query = useQuery({
        queryKey: ['getEventUserFeedback', deviceId, eventId],
        queryFn: async () => {
            const response = await OCGatewayClient.request('getEventUserFeedback', {
                deviceId,
                eventId
            }) as Partial<EventUserFeedback>;

            return response ? new EventUserFeedback(response) : null;
        },
        staleTime: 1000 * 60 * 5, // 5 minutes
        gcTime: 1000 * 60 * 60 * 24,
        networkMode: 'always',
        retry: false
    });

    return query;
}

export function useDeleteEventUserFeedbackMutation(deviceId: string, eventId: number) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ['deleteEventUserFeedback', deviceId, eventId],
        mutationFn: async () => {
            return OCGatewayClient.request('deleteEventUserFeedback', {
                deviceId,
                eventId
            }) as Promise<void>;
        },
        onSuccess: () => {
            // Clear the feedback from the query cache
            queryClient.setQueryData(['getEventUserFeedback', deviceId, eventId], null);
        },
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: ['getEventUserFeedback', deviceId, eventId]
            });
            
            // Invalidate events queries that have isContraband criteria
            queryClient.invalidateQueries({
                queryKey: ['getEvents'],
                predicate: (query) => {
                    const criteria = query.queryKey[1] as any;
                    return criteria?.isContraband !== undefined;
                }
            });
        }
    });
}

export function useUpdateEventUserFeedbackMutation(deviceId: string, eventId: number) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ['updateEventUserFeedback', {
            deviceId,
            eventId
        }],
        mutationFn: async (feedback: Partial<EventUserFeedback>) => {
            const response = await OCGatewayClient.request('updateEventUserFeedback', {
                deviceId,
                eventId,
                ...feedback
            }) as Partial<EventUserFeedback>;

            return new EventUserFeedback(response);
        },
        onSuccess: (data) => {
            // Update the query cache with the new feedback data
            queryClient.setQueryData(['getEventUserFeedback', deviceId, eventId], data);
        },
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: ['getEventUserFeedback', deviceId, eventId]
            });
            
            // Invalidate events queries that have isContraband criteria
            queryClient.invalidateQueries({
                queryKey: ['getEvents'],
                predicate: (query) => {
                    const criteria = query.queryKey[1] as any;
                    return criteria?.isContraband !== undefined;
                }
            });
        }
    });
}